import { openDB } from 'idb';

import logger from './logger';

const DB_NAME = 'meetingRecordings';
const STORE_NAME = 'chunks';
const DB_VERSION = 1; // Note for dev: Increment this when making DB schema changes
const DB_INDEX = 'meetingName';

export const initRecordingDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion) {
      // Create 'chunks' if it doesn't exist
      if (!db.objectStoreNames.contains('chunks')) {
        const store = db.createObjectStore('chunks', { keyPath: 'id' });
        store.createIndex(DB_INDEX, DB_INDEX);
      }

      // Create 'metadata' if it doesn't exist
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: DB_INDEX });
      }
    }
  });
};

export const saveMeetingMetadata = async (meetingName: string, data: any) => {
  const db = await initRecordingDB();
  await db.put('metadata', { meetingName, ...data });
};

export const getMeetingMetadata = async (meetingName: string) => {
  const db = await initRecordingDB();
  return db.get('metadata', meetingName);
};

export const saveChunk = async (
  meetingName: string,
  chunkIndex: number,
  blob: Blob,
  sessionId?: string
) => {
  try {
    const db = await initRecordingDB();
    await db.put(STORE_NAME, {
      id: `${meetingName}-${chunkIndex}`,
      meetingName,
      chunkIndex,
      blob,
      sessionId: sessionId || 'default',
      createdAt: Date.now()
    });
  } catch (err) {
    logger.error('Failed to save chunk', err);
    throw err;
  }
};

export const getLastChunkIndex = async (meetingName: string) => {
  const db = await initRecordingDB();

  const chunks = await db.getAllFromIndex('chunks', DB_INDEX, meetingName);

  if (!chunks || chunks.length === 0) {
    return 0;
  }

  return Math.max(...chunks.map((c) => c.chunkIndex)) + 1;
};

export const getMeetingChunks = async (meetingName: string) => {
  const db = await initRecordingDB();
  return db.getAllFromIndex(STORE_NAME, DB_INDEX, meetingName);
};

const MIN_REQUIRED_STORAGE_BYTES = 300 * 1024 * 1024; // 300 MB

export const checkIndexedDBHealth = async (): Promise<boolean> => {
  try {
    const db = await initRecordingDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await tx.store.put({
      id: '__health_check__',
      meetingName: '__health_check__',
      chunkIndex: 0,
      blob: new Blob([]),
      sessionId: 'health',
      createdAt: Date.now()
    });
    await tx.done;
    await db.delete(STORE_NAME, '__health_check__');

    if (navigator.storage?.estimate) {
      const { quota = 0, usage = 0 } = await navigator.storage.estimate();
      const available = quota - usage;
      if (available < MIN_REQUIRED_STORAGE_BYTES) {
        logger.error(
          `Insufficient storage: ${Math.round(available / 1024 / 1024)}MB available, ${Math.round(MIN_REQUIRED_STORAGE_BYTES / 1024 / 1024)}MB required`
        );
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
};

export const deleteMeetingData = async (meetingName: string) => {
  const db = await initRecordingDB();

  const tx = db.transaction(STORE_NAME, 'readwrite');
  const index = tx.store.index(DB_INDEX);
  const chunks = await index.getAllKeys(meetingName);
  for (const key of chunks) {
    await tx.store.delete(key);
  }
  await tx.done;

  await db.delete('metadata', meetingName);
};
